/**
 * @file Manages the "Drugs" tab within the Settings view (Admin UI).
 * Handles CRUD for Drug Inventory, Locations, Roles, and Approval Workflows.
 */

import { appData, state, tempConfigData, setTempConfigData, tempUsersData } from '../../app/state/state.js';
import {
    openDrugInventoryForm,
    openDrugRoleForm,
} from './drugAdminModals.js';
import { openConfirmationModal } from '../../shared/ui/confirmationModal.js';
import { db } from '../../app/firebase/firebase.js';
import { updateDoc, doc, deleteDoc, writeBatch } from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { saveDrugRoleSettingsCallable } from "../../app/firebase/callables.js";
import {
    escapeHTML,
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    getReadOnlyValueHtml,
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    createSelectControl,
} from "../../shared/ui/controls/selectControl.js";
import {
    destroyRegisteredControlsIn,
} from "../../shared/ui/controls/controlCore.js";
import {
    applyDrugControlSurface,
    bindDrugNativeFieldSurface,
    DRUG_MODAL_INPUT_CLASSES,
    DRUG_PRIMARY_BUTTON_CLASSES,
} from "./drugControlStyles.js";

const ADMIN_CARD_CLASSES = "bg-white p-6 rounded-b-lg rounded-tr-lg border border-gray-200 shadow-sm";
const ADMIN_TABLE_INPUT_CLASSES = [
    DRUG_MODAL_INPUT_CLASSES,
    "sm:!text-xs",
].join(" ");
const ADMIN_ICON_BUTTON_CLASSES = [
    "inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400",
    "transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]",
].join(" ");
const ADMIN_DANGER_ICON_BUTTON_CLASSES = [
    "inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400",
    "transition-colors hover:text-red-600 focus:outline-none focus-visible:text-red-600",
].join(" ");
const ADMIN_RESTORE_ICON_BUTTON_CLASSES = [
    "inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400",
    "transition-colors hover:text-green-600 focus:outline-none focus-visible:text-green-600",
].join(" ");
const ADMIN_ARCHIVE_ICON_BUTTON_CLASSES = [
    "inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400",
    "transition-colors hover:text-orange-500 focus:outline-none focus-visible:text-orange-500",
].join(" ");
const ADMIN_ORDER_BUTTON_CLASSES = [
    "inline-flex h-6 min-h-6 w-6 min-w-6 shrink-0 items-center justify-center rounded-full text-gray-400",
    "transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)]",
    "focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]",
    "disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400",
].join(" ");
const ADMIN_SETTING_ROW_CLASSES = [
    "grid grid-cols-1 gap-2 py-3 sm:grid-cols-[minmax(0,1fr)_10rem]",
    "sm:items-center sm:gap-4",
].join(" ");
const ADMIN_SETTING_CONTROL_CLASSES = "sm:justify-self-stretch";
const ADMIN_SUB_TAB_ACTIVE_CLASSES =
    "relative z-10 -mb-px rounded-t-md rounded-b-none border border-gray-200 border-b-white bg-white text-[var(--dfc-brand-accent)]";
const ADMIN_SUB_TAB_INACTIVE_CLASSES =
    "rounded-t-md rounded-b-none border border-transparent text-gray-600 hover:bg-gray-100";
const ADMIN_COMPACT_PRIMARY_BUTTON_CLASSES = [
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[var(--dfc-brand-primary)]",
    "bg-[var(--dfc-brand-primary)] px-4 py-1 text-sm font-semibold text-[var(--dfc-brand-primary-contrast)] transition-colors",
    "hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none focus-visible:bg-[var(--dfc-brand-primary-hover)]",
    "sm:min-h-[30px] disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");
const ADMIN_COMPACT_SECONDARY_BUTTON_CLASSES = [
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-gray-200",
    "bg-white px-4 py-1 text-sm font-medium text-gray-600 transition-colors",
    "hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)]",
    "focus:outline-none focus-visible:border-[var(--dfc-brand-primary)] focus-visible:text-[var(--dfc-brand-primary)]",
    "sm:min-h-[30px] disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");
const ADMIN_MODAL_PRIMARY_BUTTON_CLASSES = [
    "inline-flex h-9 items-center justify-center rounded-md border border-[var(--dfc-brand-primary)]",
    "bg-[var(--dfc-brand-primary)] px-4 text-sm font-semibold text-[var(--dfc-brand-primary-contrast)] transition-colors",
    "hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none focus-visible:bg-[var(--dfc-brand-primary-hover)]",
    "disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");
const ADMIN_MODAL_SECONDARY_BUTTON_CLASSES = [
    "inline-flex h-9 items-center justify-center rounded-md bg-gray-200 px-4",
    "text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-300 focus:outline-none",
].join(" ");
const NO_ROLE_VALUE = "__no_drug_role__";
const ANY_ROLE_VALUE = "__any_drug_role__";
const GLOBAL_CONTEXT_VALUE = "__global_context__";

function dispatchDrugSettingsInput() {
    document.getElementById('drug-settings-content')?.dispatchEvent(new Event('input', { bubbles: true }));
}

function getOrderValue(item, field = 'order', fallback = 999) {
    const value = Number(item?.[field]);
    return Number.isFinite(value) ? value : fallback;
}

function getItemId(item) {
    return String(item?.id ?? item?.uid ?? "");
}

function getOrderLabel(item) {
    return String(item?.shortName || item?.name || item?.id || item?.uid || "")
        .toLowerCase();
}

function compareOrderLabels(a, b) {
    return getOrderLabel(a.item).localeCompare(getOrderLabel(b.item));
}

function getOrderedEntries(items, options = {}) {
    const {
        field = 'order',
        fallback = 999,
        visibleFilter = () => true,
        tieBreaker = (a, b) => a.index - b.index,
    } = options;

    return (items || [])
        .map((item, index) => {
            const rawOrder = Number(item?.[field]);
            return {
                index,
                item,
                order: Number.isFinite(rawOrder) ? rawOrder : fallback + index,
            };
        })
        .filter((entry) => visibleFilter(entry.item))
        .sort((a, b) => {
            const orderDiff = a.order - b.order;
            if (orderDiff !== 0) { return orderDiff; }
            const tieDiff = tieBreaker(a, b);
            return tieDiff !== 0 ? tieDiff : a.index - b.index;
        });
}

function getSortedOrderedItems(items, options = {}) {
    return getOrderedEntries(items, options).map((entry) => entry.item);
}

function getNextOrder(items, field = 'order') {
    const maxOrder = (items || []).reduce((max, item) =>
        Math.max(max, getOrderValue(item, field, 0)), 0);
    return maxOrder + 1;
}

function renderOrderControls({ scope, id, label, isFirst, isLast }) {
    const safeLabel = escapeHTML(label || 'item');
    return `
        <div class="drug-order-controls flex flex-col items-center" aria-label="Order controls">
            <button type="button" class="drug-order-btn ${ADMIN_ORDER_BUTTON_CLASSES}" data-order-scope="${scope}" data-id="${escapeHTML(id)}" data-direction="-1" title="Move up" aria-label="Move ${safeLabel} up" ${isFirst ? 'disabled' : ''}>
                <span class="material-symbols-outlined text-[16px] leading-none">expand_less</span>
            </button>
            <button type="button" class="drug-order-btn ${ADMIN_ORDER_BUTTON_CLASSES}" data-order-scope="${scope}" data-id="${escapeHTML(id)}" data-direction="1" title="Move down" aria-label="Move ${safeLabel} down" ${isLast ? 'disabled' : ''}>
                <span class="material-symbols-outlined text-[16px] leading-none">expand_more</span>
            </button>
        </div>
    `;
}

function moveOrderedItem(items, id, direction, options = {}) {
    const {
        field = 'order',
        fallback = 999,
        visibleFilter = () => true,
        groupFilter = () => true,
        tieBreaker = (a, b) => a.index - b.index,
    } = options;
    const itemId = String(id);
    const sorted = getOrderedEntries(items, { field, fallback, tieBreaker });
    const currentEntry = sorted.find((entry) => getItemId(entry.item) === itemId);
    if (!currentEntry || !visibleFilter(currentEntry.item)) { return false; }

    const group = sorted.filter((entry) =>
        visibleFilter(entry.item) &&
        groupFilter(entry.item, currentEntry.item, entry, currentEntry));
    const visibleIndex = group.findIndex((entry) => getItemId(entry.item) === itemId);
    const targetIndex = visibleIndex + direction;
    if (
        visibleIndex === -1 ||
        targetIndex < 0 ||
        targetIndex >= group.length
    ) {
        return false;
    }

    const nextOrder = [...group];
    const [movedEntry] = nextOrder.splice(visibleIndex, 1);
    nextOrder.splice(targetIndex, 0, movedEntry);

    nextOrder.forEach((entry, position) => {
        if (typeof entry.item === 'object' && entry.item !== null) {
            entry.item[field] = position + 1;
        }
    });
    return true;
}

function hasSameActiveState(item, currentItem) {
    return (item?.isActive !== false) === (currentItem?.isActive !== false);
}

function captureOrderValues(items, field = 'order') {
    return new Map((items || []).map((item) => [
        getItemId(item),
        item?.[field],
    ]));
}

function orderValueChanged(previousValue, nextValue) {
    const previousNumber = Number(previousValue);
    const nextNumber = Number(nextValue);
    if (Number.isFinite(previousNumber) && Number.isFinite(nextNumber)) {
        return previousNumber !== nextNumber;
    }
    return previousValue !== nextValue;
}

function restoreOrderValues(items, previous, field = 'order') {
    (items || []).forEach((item) => {
        const itemId = getItemId(item);
        if (!itemId || !previous.has(itemId)) { return; }
        const previousValue = previous.get(itemId);
        if (previousValue === undefined) {
            delete item[field];
            return;
        }
        item[field] = previousValue;
    });
}

async function persistFirestoreOrder(items, previous, options) {
    const { collectionName, field = 'order' } = options;
    const changed = (items || []).filter((item) =>
        orderValueChanged(previous.get(getItemId(item)), item?.[field]));
    if (changed.length === 0) { return; }

    const batch = writeBatch(db);
    changed.forEach((item) => {
        batch.update(doc(db, collectionName, item.id), { [field]: item[field] });
    });
    await batch.commit();
}

function getRoleOptions(roles, options = {}) {
    const includeNoAccess = options.includeNoAccess === true;
    const baseOptions = roles.map((role) => ({
        value: role.id,
        label: role.name || role.id,
        order: Number(role.rank ?? 99),
        searchText: `${role.name || ""} ${role.id || ""}`,
    }));

    return includeNoAccess ?
        [{ value: NO_ROLE_VALUE, label: "No Access", order: -1 }, ...baseOptions] :
        baseOptions;
}

function getApprovalModeOptions(workflowKey) {
    const allowPostAction = ![
        'drug_void_transaction',
        'drug_audit_start',
        'drug_audit_finalize',
        'drug_audit_cancel',
    ].includes(workflowKey);
    return [
        { value: 'reject', label: 'Reject Transaction (Strict Block)' },
        { value: 'pre_action', label: 'Pre-Action Approval (Hard Block)' },
        ...(allowPostAction ? [{ value: 'post_action', label: 'Post-Action Approval (Soft)' }] : []),
        { value: 'bypass', label: 'No Approval Required' },
    ];
}

function getRuleApprovalModeOptions(workflowKey) {
    return getApprovalModeOptions(workflowKey).map((option) => ({
        ...option,
        label: option.value === 'reject' ? 'Reject Transaction (Strict)' :
            option.value === 'pre_action' ? 'Pre-Action (Hard Block)' :
                option.value === 'post_action' ? 'Post-Action (Soft)' :
                    'Bypass (Auto-Approve)',
    }));
}

function getRuleTargetOptions(roles, isTargetContextValid) {
    return [
        { value: 'escalation_path', label: 'Escalation Path (Default)', order: -2 },
        ...(isTargetContextValid ?
            [{ value: 'field:provider', label: 'Target User', order: -1 }] :
            []),
        ...getRoleOptions(roles),
    ];
}

function getAdminCheckboxHtml({ id, checked = false, label }) {
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
            <input type="checkbox" id="${id}" class="${NATIVE_CHECKBOX_CLASSES}" ${checked ? 'checked' : ''}>
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
            </span>
            <span>${label}</span>
        </label>
    `;
}

// --- Local State for Sub-Tabs ---
let currentDrugsTab = 'general'; // 'general', 'inventory', 'locations', 'roles', 'workflows'
let selectedWorkflowKey = 'drug_use'; // Tracks active workflow in Rule Builder

/**
 * Main Entry Point: Renders the Drug Settings Container.
 * @param {HTMLElement} container - The container element from settings.js
 */
export function renderDrugsSettings(container) {
    applyDrugControlSurface(container, appData);
    container.innerHTML = `
        <div class="flex flex-col h-full">
            <!-- Sub-Navigation -->
            <div id="drug-sub-tabs" class="flex items-center gap-2 overflow-x-auto overflow-y-hidden">
                ${renderSubTabBtn('general', 'General')}
                ${renderSubTabBtn('inventory', 'Inventory')}
                ${renderSubTabBtn('locations', 'Locations')}
                ${renderSubTabBtn('roles', 'Roles')}
                ${renderSubTabBtn('workflows', 'Workflows')}
            </div>

            <!-- Content Area -->
            <div id="drug-settings-content" class="flex-grow overflow-y-auto">
                <!-- Dynamic Content Loads Here -->
            </div>
        </div>
    `;

    // Attach Listeners for Sub-Tabs
    const tabContainer = container.querySelector('#drug-sub-tabs');
    tabContainer.querySelectorAll('.drug-sub-tab-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            currentDrugsTab = e.target.dataset.tab;
            updateSubTabs(tabContainer);
            renderCurrentDrugsTab();
        });
    });

    // Initial Render
    renderCurrentDrugsTab();
}

/**
 * Updates the visual state of sub-tabs.
 * @param {HTMLElement} container
 */
function updateSubTabs(container) {
    container.querySelectorAll('.drug-sub-tab-btn').forEach(btn => {
        const isActive = btn.dataset.tab === currentDrugsTab;
        if (isActive) {
            btn.className = `drug-sub-tab-btn shrink-0 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap ${ADMIN_SUB_TAB_ACTIVE_CLASSES}`;
        } else {
            btn.className = `drug-sub-tab-btn shrink-0 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap ${ADMIN_SUB_TAB_INACTIVE_CLASSES}`;
        }
    });
}

function renderSubTabBtn(id, label) {
    const isActive = currentDrugsTab === id;
    const activeClass = isActive
        ? ADMIN_SUB_TAB_ACTIVE_CLASSES
        : ADMIN_SUB_TAB_INACTIVE_CLASSES;

    return `
        <button class="drug-sub-tab-btn shrink-0 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap ${activeClass}"
                data-tab="${id}">
            ${label}
        </button>
    `;
}

function renderCurrentDrugsTab() {
    const currentContainer = document.getElementById('drug-settings-content');
    if (!currentContainer) {return;}
    destroyRegisteredControlsIn(currentContainer);

    const container = currentContainer.cloneNode(false);
    currentContainer.replaceWith(container);

    switch (currentDrugsTab) {
        case 'general':
            renderGeneralSettingsPanel(container);
            break;
        case 'inventory':
            renderDrugInventoryPanel(container);
            break;
        case 'locations':
            renderLocationsPanel(container);
            break;
        case 'roles':
            renderDrugRolesPanel(container);
            break;
        case 'workflows':
            renderWorkflowsPanel(container);
            break;
        default:
            container.innerHTML = '<p class="text-gray-500">Select a tab.</p>';
    }
}

// ==========================================
// 0. General Settings Panel
// ==========================================
function renderGeneralSettingsPanel(container) {
    if (!tempConfigData.drugs) {tempConfigData.drugs = {};}
    const drugsConfig = tempConfigData.drugs;

    container.innerHTML = `
        <div class="${ADMIN_CARD_CLASSES}">
            <h3 class="text-lg font-semibold text-gray-700 mb-4">General Settings</h3>

            <div class="space-y-6 max-w-2xl">
                <!-- Forecast Section -->
                <div>
                    <h4 class="text-sm font-bold text-gray-700 mb-2">Forecasting & Usage</h4>
                    <div class="${ADMIN_SETTING_ROW_CLASSES}">
                        <div>
                            <label class="block text-sm font-medium text-gray-700" for="input-forecastEnabled">Enable stock forecasting logic</label>
                        </div>
                        <label class="form-switch ${ADMIN_SETTING_CONTROL_CLASSES}">
                            <input type="checkbox" id="input-forecastEnabled" ${drugsConfig.forecastEnabled ? 'checked' : ''}>
                            <span class="slider round"></span>
                        </label>
                    </div>

                    ${drugsConfig.forecastEnabled ? `
                    <div class="${ADMIN_SETTING_ROW_CLASSES}">
                         <label class="block text-sm font-medium text-gray-700" for="input-forecastDays">Forecast Period (Days)</label>
                         <input type="number" id="input-forecastDays" class="${DRUG_MODAL_INPUT_CLASSES}" value="${drugsConfig.forecastDays || 90}">
                    </div>
                    ` : ''}

                    <div class="${ADMIN_SETTING_ROW_CLASSES}">
                         <div>
                            <label class="block text-sm font-medium text-gray-700" for="input-bulkUseEnabled">Enable bulk usage entry</label>
                        </div>
                         <label class="form-switch ${ADMIN_SETTING_CONTROL_CLASSES}">
                            <input type="checkbox" id="input-bulkUseEnabled" ${drugsConfig.bulkUseEnabled ? 'checked' : ''}>
                            <span class="slider round"></span>
                        </label>
                    </div>
                </div>

                <!-- Compliance Section -->
                 <div class="border-t border-gray-200 pt-6">
                    <h4 class="text-sm font-bold text-gray-700 mb-2">Compliance & Audits</h4>

                    <div class="${ADMIN_SETTING_ROW_CLASSES}">
                        <div>
                            <label class="block text-sm font-medium text-gray-700" for="input-auditFrequencyDays">Audit Reminder Frequency (Days)</label>
                            <p class="text-xs text-gray-500 mt-1" data-native-field-skip="true">Days before a stock check is flagged as due.</p>
                        </div>
                        <input type="number" id="input-auditFrequencyDays" class="${DRUG_MODAL_INPUT_CLASSES}" value="${drugsConfig.auditFrequencyDays || 7}">
                    </div>

                     <div class="${ADMIN_SETTING_ROW_CLASSES}">
                        <div>
                            <label class="block text-sm font-medium text-gray-700" for="input-duplicateDetectionWindowDays">Duplicate Detection (Days)</label>
                            <p class="text-xs text-gray-500 mt-1" data-native-field-skip="true">Time window to warn about potential duplicate transactions.</p>
                        </div>
                        <input type="number" id="input-duplicateDetectionWindowDays" class="${DRUG_MODAL_INPUT_CLASSES}" value="${drugsConfig.duplicateDetectionWindowDays || 1}">
                    </div>

                    <div class="${ADMIN_SETTING_ROW_CLASSES}">
                        <div>
                            <label class="block text-sm font-medium text-gray-700" for="input-restockWindowHours">Restock Window (Hours)</label>
                            <p class="text-xs text-gray-500 mt-1" data-native-field-skip="true">Hours after a finalised audit that restock is allowed.</p>
                        </div>
                        <input type="number" id="input-restockWindowHours" class="${DRUG_MODAL_INPUT_CLASSES}" value="${drugsConfig.auditRequirements?.restockWindowHours || 24}">
                    </div>
                </div>
            </div>
        </div>
    `;
    bindDrugNativeFieldSurface(container);

    // Listeners
    const dispatchSave = () => {
        document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
    };

    const config = tempConfigData.drugs;

    const setupListener = (id, key, type = 'value', parentKey = null) => {
        const el = container.querySelector(`#${id}`);
        if (el) {
            el.addEventListener('change', (e) => {
                const val = type === 'checkbox' ? e.target.checked : Number(e.target.value);
                if (parentKey) {
                    if (!config[parentKey]) {config[parentKey] = {};}
                    config[parentKey][key] = val;
                } else {
                    config[key] = val;
                }
                dispatchSave();
                // Rerender if toggle affects visibility (Forecast)
                if (key === 'forecastEnabled') {renderGeneralSettingsPanel(container);}
            });
        }
    };

    setupListener('input-auditFrequencyDays', 'auditFrequencyDays');
    setupListener('input-duplicateDetectionWindowDays', 'duplicateDetectionWindowDays');
    setupListener('input-restockWindowHours', 'restockWindowHours', 'value', 'auditRequirements');
    setupListener('input-forecastDays', 'forecastDays');
    setupListener('input-forecastEnabled', 'forecastEnabled', 'checkbox');
    setupListener('input-bulkUseEnabled', 'bulkUseEnabled', 'checkbox');
}

// ==========================================
// 1. Drug Inventory Panel
// ==========================================
function renderDrugInventoryPanel(container) {
    container.innerHTML = `
        <div class="${ADMIN_CARD_CLASSES}">
            <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-semibold text-gray-700">Drug Inventory</h3>
                <button id="add-drug-btn" class="${DRUG_PRIMARY_BUTTON_CLASSES}">
                    <span class="material-symbols-outlined text-lg">add</span>
                    Add Drug
                </button>
            </div>

            <h3 class="text-lg font-semibold text-gray-700 mb-4">Drugs List</h3>
            <div class="flex justify-between md:items-center flex-col md:flex-row mb-4 gap-2">
                <div>
                   <p class="text-sm text-gray-500">Manage the list of controlled drugs. Edit details.</p>
                </div>
                <div class="flex items-center">
                    ${getAdminCheckboxHtml({
                        id: 'show-archived-drugs',
                        checked: state.viewSettings?.showArchivedDrugs || false,
                        label: 'Show Inactive',
                    })}
                </div>
            </div>

            <div class="overflow-hidden rounded-lg border mb-8">
                <div class="overflow-x-auto">
                    <table class="w-full min-w-[680px] text-sm text-left">
                        <thead class="bg-gray-50 text-gray-500 font-medium border-b">
                            <tr>
                                <th class="py-2 px-4 w-12 text-center"><span class="sr-only">Order</span></th>
                                <th class="py-2 px-4">Name</th>
                                <th class="py-2 px-4">Strength/Pack</th>
                                <th class="py-2 px-4">Status</th>
                                <th class="py-2 px-4 text-center">Stock</th>
                                <th class="py-2 px-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100">
                            ${renderDrugInventoryRows()}
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Suppliers Section -->
            <div class="mb-8 border-t pt-8">
                <div class="flex justify-between md:items-center flex-col md:flex-row mb-4">
                     <div>
                        <h4 class="text-base font-semibold text-gray-700">Suppliers</h4>
                        <p class="text-sm text-gray-500">Manage approved suppliers for restock orders.</p>
                    </div>
                </div>

                <div class="overflow-hidden rounded-lg border mb-4">
                    <div class="overflow-x-auto">
                        <table class="w-full min-w-[620px] text-sm text-left">
                            <thead class="bg-gray-50 text-gray-500 font-medium border-b">
                                <tr>
                                    <th class="py-2 px-4 w-12 text-center"><span class="sr-only">Order</span></th>
                                    <th class="py-2 px-4">Name</th>
                                    <th class="py-2 px-4">Address</th>
                                    <th class="py-2 px-4 text-center">Status</th>
                                    <th class="py-2 px-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody id="suppliers-tbody" class="divide-y divide-gray-100">
                                ${renderSupplierRows()}
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Add Supplier -->
                <div class="flex flex-col gap-4 bg-gray-50 p-4 rounded-lg border border-gray-100 md:flex-row md:items-end">
                    <div class="flex-grow grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div>
                            <label class="block text-base text-gray-500 sm:text-sm mb-1">Name</label>
                            <input type="text" id="new-supplier-name" class="${DRUG_MODAL_INPUT_CLASSES}" placeholder="Supplier Name">
                        </div>
                         <div>
                            <label class="block text-base text-gray-500 sm:text-sm mb-1">Address</label>
                            <input type="text" id="new-supplier-address" class="${DRUG_MODAL_INPUT_CLASSES}" placeholder="Address">
                        </div>
                    </div>
                    <button id="btn-add-supplier" class="${ADMIN_COMPACT_PRIMARY_BUTTON_CLASSES}">
                        <span class="material-symbols-outlined text-lg">add</span>
                        Add
                    </button>
                </div>
            </div>
        </div>
    `;
    bindDrugNativeFieldSurface(container);

    // Attach Listeners
    container.querySelector('#add-drug-btn').addEventListener('click', () => {
        openDrugInventoryForm();
    });

    const showArchivedToggle = container.querySelector('#show-archived-drugs');
    if (showArchivedToggle) {
        showArchivedToggle.checked = state.viewSettings?.showArchivedDrugs || false;
        showArchivedToggle.addEventListener('change', (e) => {
            if (!state.viewSettings) {state.viewSettings = {};}
            state.viewSettings.showArchivedDrugs = e.target.checked;
            renderCurrentDrugsTab();
        });
    }

    // Delegated listener for Edit buttons
    container.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) {return;}

        if (btn.classList.contains('drug-order-btn')) {
            handleDrugOrderingClick(btn);
        } else if (btn.classList.contains('edit-drug-btn')) {
            const drugId = btn.dataset.id;
            const drug = appData.drugInventory.find(d => d.id === drugId);
            if (drug) {
                openDrugInventoryForm(drug);
            }
        } else if (btn.classList.contains('action-drug-btn')) {
            const id = btn.dataset.id;
            const action = btn.dataset.action; // 'archive', 'restore', 'delete'
            handleDrugAction(id, action);
        }
    });

    // Supplier Listeners
    container.querySelector('#btn-add-supplier').addEventListener('click', handleAddSupplier);

    const suppliersTbody = container.querySelector('#suppliers-tbody');
    if (suppliersTbody) {
        // Edit / Delete Delegation
        suppliersTbody.addEventListener('click', (e) => {
            const btn = e.target.closest('button');
            if (!btn) {return;}
            const id = btn.dataset.id;
            if (btn.classList.contains('toggle-supplier-btn')) {
                const status = btn.dataset.status === 'true'; // Current status (isInactive)
                handleToggleSupplier(id, !status); // If inactive (true), set to active (true) -> wait, logic check
                // Logic:
                // isInactive = true -> we want to Restore (isActive = true)
                // isInactive = false -> we want to Archive (isActive = false)
                // handleToggleSupplier(id, shouldBeActive)
            }
        });

        // Input Change Delegation
        suppliersTbody.addEventListener('change', (e) => {
            if (e.target.classList.contains('supplier-input')) {
                const id = e.target.dataset.id;
                const field = e.target.dataset.field;
                const val = field === 'order' ? Number(e.target.value) : e.target.value;
                handleUpdateSupplier(id, field, val);
            }
        });
    }
}

function renderSupplierRows() {
    const suppliers = tempConfigData.drugs.suppliers || [];
    if (suppliers.length === 0) {return '<tr><td colspan="5" class="py-4 text-center text-gray-500 italic">No suppliers defined.</td></tr>';}

    const showArchived = document.getElementById('show-archived-drugs')?.checked || false;

    // Filter based on active status unless showArchived is true
    const visibleSuppliers = suppliers.filter(s => showArchived || s.isActive !== false);

    if (visibleSuppliers.length === 0) {return '<tr><td colspan="5" class="py-4 text-center text-gray-500 italic">No active suppliers found.</td></tr>';}

    const sortedSuppliers = getSortedOrderedItems(visibleSuppliers, {
        tieBreaker: compareOrderLabels,
    });
    return sortedSuppliers.map((sup, index) => {
        const isInactive = sup.isActive === false;
        return `
        <tr class="hover:bg-gray-50 group ${isInactive ? 'opacity-60 bg-gray-50' : ''}">
            <td class="py-2 px-4 text-center">
                ${renderOrderControls({
        scope: 'supplier',
        id: sup.id,
        label: sup.name,
        isFirst: index === 0,
        isLast: index === sortedSuppliers.length - 1,
    })}
            </td>
            <td class="py-2 px-4">
                <input type="text" class="supplier-input ${ADMIN_TABLE_INPUT_CLASSES} ${isInactive ? 'line-through text-gray-500' : ''}" value="${escapeHTML(sup.name)}" data-id="${escapeHTML(sup.id)}" data-field="name">
            </td>
            <td class="py-2 px-4">
                <input type="text" class="supplier-input ${ADMIN_TABLE_INPUT_CLASSES}" value="${escapeHTML(sup.address || '')}" data-id="${escapeHTML(sup.id)}" data-field="address">
            </td>
            <td class="py-2 px-4 text-center">
                 <span class="text-xs px-2 py-1 rounded ${isInactive ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}">
                    ${isInactive ? 'Inactive' : 'Active'}
                 </span>
            </td>
            <td class="py-2 px-4 text-right">
                <button class="toggle-supplier-btn ${isInactive ? ADMIN_RESTORE_ICON_BUTTON_CLASSES : ADMIN_ARCHIVE_ICON_BUTTON_CLASSES}" title="${isInactive ? 'Restore' : 'Archive'}" data-id="${escapeHTML(sup.id)}" data-status="${isInactive}">
                    <span class="material-symbols-outlined text-lg">${isInactive ? 'restore_from_trash' : 'archive'}</span>
                </button>
            </td>
        </tr>
    `}).join('');
}

function handleAddSupplier() {
    const nameInput = document.getElementById('new-supplier-name');
    const addrInput = document.getElementById('new-supplier-address');

    const name = nameInput.value.trim();
    if (!name) {return;}

    if (!tempConfigData.drugs.suppliers) {tempConfigData.drugs.suppliers = [];}

    const newSupplier = {
        id: `sup_${Date.now()}`,
        name: name,
        address: addrInput.value.trim(),
        isActive: true, // Default to active
        order: getNextOrder(tempConfigData.drugs.suppliers)
    };

    tempConfigData.drugs.suppliers.push(newSupplier);
    setTempConfigData(tempConfigData);

    // Clear inputs
    nameInput.value = '';
    addrInput.value = '';

    // Refresh UI
    renderCurrentDrugsTab();
    // Trigger Save
    document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
}

function handleUpdateSupplier(id, field, value) {
    const sup = tempConfigData.drugs.suppliers.find(s => s.id === id);
    if (sup) {
        sup[field] = value;
        setTempConfigData(tempConfigData);
        // Trigger Save
        document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
    }
}

function handleToggleSupplier(id) {
    const sup = tempConfigData.drugs.suppliers.find(s => s.id === id);
    if (sup) {
        // Toggle status
        sup.isActive = !(sup.isActive !== false); // Default true, so if undefined it becomes false

        setTempConfigData(tempConfigData);
        renderCurrentDrugsTab();
        document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
    }
}

function renderDrugInventoryRows() {
    const drugs = appData.drugInventory || [];
    const showArchived = state.viewSettings?.showArchivedDrugs || false;

    // Sort by order
    const sortedDrugs = getSortedOrderedItems(drugs, {
        fallback: 9999,
        tieBreaker: compareOrderLabels,
    });

    if (sortedDrugs.length === 0) {
        return '<tr><td colspan="6" class="py-4 text-center text-gray-500 italic">No drugs found.</td></tr>';
    }

    const visibleDrugs = sortedDrugs.filter((drug) =>
        showArchived || drug.isActive !== false);

    return sortedDrugs.map((drug) => {
        const isActive = drug.isActive !== false;

        if (!isActive && !showArchived) {return '';}
        const visibleIndex = visibleDrugs.findIndex((entry) => entry.id === drug.id);

        const statusBadge = isActive
            ? '<span class="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs">Active</span>'
            : '<span class="px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 text-xs">Archived</span>';

        // Calculate total stock from cached levels
        const totalStock = Object.values(drug.stockLevels || {}).reduce((sum, val) => sum + (val || 0), 0);

        // Determine available actions based on usage history
        const hasUsage = isDrugUsed(drug.id);

        let actionButtons = `
            <button class="edit-drug-btn ${ADMIN_ICON_BUTTON_CLASSES}" title="Edit" data-id="${escapeHTML(drug.id)}">
                <span class="material-symbols-outlined text-lg">edit</span>
            </button>
        `;

        if (hasUsage) {
            // If used: Show Archive/Restore toggle
            actionButtons += `
                <button class="action-drug-btn ${isActive ? ADMIN_ARCHIVE_ICON_BUTTON_CLASSES : ADMIN_RESTORE_ICON_BUTTON_CLASSES}"
                    title="${isActive ? 'Archive (Make Inactive)' : 'Restore (Make Active)'}"
                    data-id="${escapeHTML(drug.id)}" data-action="${isActive ? 'archive' : 'restore'}">
                    <span class="material-symbols-outlined text-lg">${isActive ? 'archive' : 'unarchive'}</span>
                </button>
            `;
        } else {
            // If UNUSED: Show Delete button
            actionButtons += `
                <button class="action-drug-btn ${ADMIN_DANGER_ICON_BUTTON_CLASSES}"
                    title="Delete (Unused)"
                    data-id="${escapeHTML(drug.id)}" data-action="delete">
                    <span class="material-symbols-outlined text-lg">delete</span>
                </button>
            `;
        }

        return `
            <tr class="hover:bg-gray-50 transition-colors group ${!isActive ? 'bg-gray-50 opacity-60' : ''}">
                <td class="py-3 px-4 text-center">
                    ${renderOrderControls({
        scope: 'drug',
        id: drug.id,
        label: drug.name,
        isFirst: visibleIndex === 0,
        isLast: visibleIndex === visibleDrugs.length - 1,
    })}
                </td>
                <td class="py-3 px-4">
                    <div class="font-medium text-gray-900">${sanitizeHTML(drug.name)}</div>
                    <div class="text-xs text-gray-500">${sanitizeHTML(drug.shortName || '')}</div>
                </td>
                <td class="py-3 px-4 text-gray-600">
                    ${escapeHTML(String(drug.strength ?? ""))}${escapeHTML(String(drug.strengthUnit ?? ""))} / ${escapeHTML(String(drug.packSize ?? ""))}
                </td>
                <td class="py-3 px-4">${statusBadge}</td>
                <td class="py-3 px-4 text-center font-mono font-medium ${totalStock < 0 ? 'text-red-600' : 'text-gray-700'}">
                    ${totalStock}
                </td>
                <td class="py-3 px-4 text-right flex justify-end gap-1">
                    ${actionButtons}
                </td>
            </tr>
        `;
    }).join('');
}


// ==========================================
// 2. Locations Panel
// ==========================================
function renderLocationsPanel(container) {
    if (!tempConfigData.drugs) {tempConfigData.drugs = {};}
    if (!tempConfigData.drugs.locations) {tempConfigData.drugs.locations = [];}

    const locations = getSortedOrderedItems(tempConfigData.drugs.locations, {
        tieBreaker: compareOrderLabels,
    });

    container.innerHTML = `
        <div class="${ADMIN_CARD_CLASSES}">
            <div class="flex justify-between md:items-center flex-col md:flex-row mb-6">
                <div>
                    <h3 class="text-lg font-semibold text-gray-700">Storage Locations</h3>
                    <p class="text-sm text-gray-500">Manage where controlled drugs are stored (e.g., Safe, Room 1).</p>
                </div>
                 <div class="flex items-center mt-2 md:mt-0">
                    ${getAdminCheckboxHtml({
                        id: 'show-archived-locations',
                        checked: state.viewSettings?.showArchivedLocations || false,
                        label: 'Show Inactive',
                    })}
                </div>
            </div>

            <div class="overflow-hidden rounded-lg border">
                <div class="overflow-x-auto">
                    <table class="w-full min-w-[520px] text-sm text-left text-gray-500">
                        <thead class="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                            <tr>
                                <th class="py-3 px-4 w-16 text-center"><span class="sr-only">Order</span></th>
                                <th class="py-3 px-4">Location Name</th>
                                <th class="py-3 px-4 w-24 text-center">Status</th>
                                <th class="py-3 px-4 w-24 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100">
                            ${locations.length > 0 ? renderLocationRows(locations) : '<tr><td colspan="4" class="py-4 text-center text-gray-400">No locations found.</td></tr>'}
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Add Location Form -->
            <div class="mt-6 flex flex-col gap-4 bg-gray-50 p-4 rounded-lg border border-gray-100 md:flex-row md:items-end">
                <div class="flex-grow">
                    <label class="block text-base text-gray-500 sm:text-sm mb-1">New Location Name</label>
                    <input type="text" id="new-loc-name" class="${DRUG_MODAL_INPUT_CLASSES}" placeholder="e.g. Emergency Bag">
                </div>
                <button id="btn-add-location" class="${ADMIN_COMPACT_PRIMARY_BUTTON_CLASSES}">
                    <span class="material-symbols-outlined text-lg">add</span>
                    Add
                </button>
            </div>
        </div>
    `;
    bindDrugNativeFieldSurface(container);

    // Listeners
    container.querySelector('#btn-add-location').addEventListener('click', handleAddLocation);

    const showArchivedToggle = container.querySelector('#show-archived-locations');
    if (showArchivedToggle) {
        showArchivedToggle.checked = state.viewSettings?.showArchivedLocations || false;
        showArchivedToggle.addEventListener('change', (e) => {
            if (!state.viewSettings) {state.viewSettings = {};}
            state.viewSettings.showArchivedLocations = e.target.checked;
            renderCurrentDrugsTab();
        });
    }

    // Delegation for dynamic table rows
    const tbody = container.querySelector('tbody');
    tbody.addEventListener('change', (e) => {
        if (e.target.classList.contains('loc-input')) {
            const id = e.target.dataset.id;
            const field = e.target.dataset.field;
            const val = field === 'order' ? Number(e.target.value) : e.target.value;

            const loc = tempConfigData.drugs.locations.find(l => l.id === id);
            if (loc) {
                loc[field] = val;
                // Signal change
                document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    });

    tbody.addEventListener('click', (e) => {
        const btn = e.target.closest('.action-loc-btn');
        if (e.target.closest('.drug-order-btn')) {
            handleDrugOrderingClick(e.target.closest('.drug-order-btn'));
            return;
        }
        if (btn) {
            const id = btn.dataset.id;
            const action = btn.dataset.action; // 'archive', 'restore', 'delete'
            handleLocationAction(id, action);
        }
    });
}

function renderLocationRows(locations) {
    const showArchived = state.viewSettings?.showArchivedLocations || false;

    // Sort: Order first, then Name
    const sorted = getSortedOrderedItems(locations, {
        tieBreaker: compareOrderLabels,
    });

    const visibleLocations = sorted.filter((loc) =>
        showArchived || loc.isActive !== false);

    return sorted.map(loc => {
        const isActive = loc.isActive !== false; // Default to true if undefined
        if (!isActive && !showArchived) {return '';}
        const visibleIndex = visibleLocations.findIndex((entry) => entry.id === loc.id);

        const hasUsage = isLocationUsed(loc.id);

        let actionBtn;
        if (hasUsage) {
            actionBtn = `
                <button class="action-loc-btn ${isActive ? ADMIN_ARCHIVE_ICON_BUTTON_CLASSES : ADMIN_RESTORE_ICON_BUTTON_CLASSES}"
                    title="${isActive ? 'Archive (Make Inactive)' : 'Restore (Make Active)'}"
                    data-id="${escapeHTML(loc.id)}" data-action="${isActive ? 'archive' : 'restore'}">
                    <span class="material-symbols-outlined text-lg">${isActive ? 'archive' : 'unarchive'}</span>
                </button>`;
        } else {
            actionBtn = `
                <button class="action-loc-btn ${ADMIN_DANGER_ICON_BUTTON_CLASSES}"
                    title="Delete (Unused)"
                    data-id="${escapeHTML(loc.id)}" data-action="delete">
                    <span class="material-symbols-outlined text-lg">delete</span>
                </button>`;
        }

        return `
        <tr class="${!isActive ? 'bg-gray-50 opacity-60' : ''}">
            <td class="py-2 px-4 text-center">
                ${renderOrderControls({
        scope: 'location',
        id: loc.id,
        label: loc.name,
        isFirst: visibleIndex === 0,
        isLast: visibleIndex === visibleLocations.length - 1,
    })}
            </td>
            <td class="py-2 px-4">
                <input type="text" class="loc-input ${ADMIN_TABLE_INPUT_CLASSES}" value="${escapeHTML(loc.name)}" data-id="${escapeHTML(loc.id)}" data-field="name">
            </td>
            <td class="py-2 px-4 text-center">
                ${isActive
                ? '<span class="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-medium">Active</span>'
                : '<span class="px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-medium">Archived</span>'}
            </td>
            <td class="py-2 px-4 text-center">
                ${actionBtn}
            </td>
        </tr>
    `;
    }).join('');
}

function handleAddLocation() {
    const nameInput = document.getElementById('new-loc-name');
    const name = nameInput.value.trim();
    if (!name) {return;}

    // Generate Robust Immutable ID
    const randomSuffix = Math.random().toString(36).substring(2, 7); // 5 chars
    const id = `loc_${Date.now()}_${randomSuffix}`;

    const newLoc = {
        id: id,
        name: name,
        isActive: true,
        order: getNextOrder(tempConfigData.drugs.locations)
    };

    tempConfigData.drugs.locations.push(newLoc);
    setTempConfigData(tempConfigData);

    // Refresh Panel
    renderCurrentDrugsTab();

    // Trigger Save
    document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
}

function handleLocationAction(id, action) {
    if (action === 'delete') {
        openConfirmationModal('Delete Location?', 'Are you sure you want to permanently delete this location? Since it has no usage history, it is safe to remove.', () => {
            tempConfigData.drugs.locations = tempConfigData.drugs.locations.filter(l => l.id !== id);
            setTempConfigData(tempConfigData);
            renderCurrentDrugsTab();
            document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
        });
        return;
    }

    const newStatus = action === 'restore'; // archive -> false, restore -> true

    // If Deactivating: Check for ACTIVE stock (Safety Net)
    if (newStatus === false) {
        const drugsWithStock = (appData.drugInventory || []).filter(d => {
            const stock = d.stockLevels?.[id];
            return stock && stock > 0;
        });

        if (drugsWithStock.length > 0) {
            const drugNames = drugsWithStock.map(d => d.name).slice(0, 3).join(', ') + (drugsWithStock.length > 3 ? '...' : '');
            openConfirmationModal('Cannot Archive Location', `This location contains active stock for: <b>${drugNames}</b>.<br><br>Please move or use the stock before archiving this location.`, () => { }, false, 'OK');
            return;
        }
    }

    const title = newStatus ? 'Restore Location?' : 'Archive Location?';
    const message = newStatus
        ? 'Make this location active again? It will appear in dropdowns.'
        : 'Archive this location? It will be hidden from dropdowns but historical records will be preserved.';

    openConfirmationModal(title, message, () => {
        const loc = tempConfigData.drugs.locations.find(l => l.id === id);
        if (loc) {
            loc.isActive = newStatus;
            setTempConfigData(tempConfigData);

            // Refresh Panel
            renderCurrentDrugsTab();

            // Trigger Save
            document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
        }
    }, false, newStatus ? 'Restore' : 'Archive');
}

/**
 * Handles Archive/Restore/Delete for Drugs
 */
function handleDrugAction(id, action) {
    if (action === 'delete') {
        openConfirmationModal('Delete Drug?', 'Are you sure you want to permanently delete this drug? Since it has no usage history, it is safe to remove.', async () => {
            try {
                // Optimistic Update: Remove from local state immediately
                if (appData.drugInventory) {
                    appData.drugInventory = appData.drugInventory.filter(d => d.id !== id);
                }
                renderCurrentDrugsTab();

                await deleteDoc(doc(db, 'drug_inventory', id));
                // State update handled by listener in settings.js
            } catch (error) {
                console.error("Error deleting drug:", error);
                openConfirmationModal('Error', 'Failed to delete drug.', () => {}, false, 'OK');
            }
        });
        return;
    }

    const newStatus = action === 'restore';
    const verb = newStatus ? 'Restore' : 'Archive';

    openConfirmationModal(`${verb} Drug?`,
        newStatus ? 'Make this drug active again?' : 'Archive this drug? It will be hidden from lists but history is preserved.',
        async () => {
            try {
                // Optimistic Update
                if (appData.drugInventory) {
                    const drug = appData.drugInventory.find(d => d.id === id);
                    if (drug) {
                        drug.isActive = newStatus;
                        renderCurrentDrugsTab();
                    }
                }

                await updateDoc(doc(db, 'drug_inventory', id), {
                    isActive: newStatus
                });
            } catch (error) {
                console.error("Error updating drug status:", error);
                openConfirmationModal('Error', 'Failed to update status.', () => {}, false, 'OK');
            }
        }, false, verb);
}

async function handleDrugOrderingClick(button) {
    const scope = button.dataset.orderScope;
    const id = button.dataset.id;
    const direction = Number(button.dataset.direction);
    if (!scope || !id || !Number.isFinite(direction)) { return; }

    button.disabled = true;
    try {
        if (scope === 'drug') {
            const items = appData.drugInventory || [];
            const field = 'order';
            const previous = captureOrderValues(items, field);
            const moved = moveOrderedItem(items, id, direction, {
                field,
                fallback: 9999,
                visibleFilter: (drug) =>
                    state.viewSettings?.showArchivedDrugs || drug.isActive !== false,
                groupFilter: hasSameActiveState,
                tieBreaker: compareOrderLabels,
            });
            if (!moved) { return; }
            renderCurrentDrugsTab();
            try {
                await persistFirestoreOrder(items, previous, {
                    collectionName: 'drug_inventory',
                    field,
                });
            } catch (error) {
                restoreOrderValues(items, previous, field);
                renderCurrentDrugsTab();
                throw error;
            }
            return;
        }

        if (scope === 'role') {
            const items = appData.drugRoles || [];
            const field = 'rank';
            const previous = captureOrderValues(items, field);
            const moved = moveOrderedItem(items, id, direction, {
                field,
                tieBreaker: compareOrderLabels,
            });
            if (!moved) { return; }
            renderCurrentDrugsTab();
            try {
                await persistFirestoreOrder(items, previous, {
                    collectionName: 'drug_roles',
                    field,
                });
            } catch (error) {
                restoreOrderValues(items, previous, field);
                renderCurrentDrugsTab();
                throw error;
            }
            return;
        }

        let moved = false;
        if (scope === 'supplier') {
            moved = moveOrderedItem(tempConfigData.drugs.suppliers || [], id, direction, {
                visibleFilter: (supplier) =>
                    state.viewSettings?.showArchivedDrugs || supplier.isActive !== false,
                groupFilter: hasSameActiveState,
                tieBreaker: compareOrderLabels,
            });
        } else if (scope === 'location') {
            moved = moveOrderedItem(tempConfigData.drugs.locations || [], id, direction, {
                visibleFilter: (location) =>
                    state.viewSettings?.showArchivedLocations || location.isActive !== false,
                groupFilter: hasSameActiveState,
                tieBreaker: compareOrderLabels,
            });
        } else if (scope === 'user') {
            moved = moveOrderedItem(tempUsersData || [], id, direction, {
                field: 'drugSortOrder',
                visibleFilter: (user) => user?.isActive !== false,
                tieBreaker: compareOrderLabels,
            });
        }
        if (!moved) { return; }

        setTempConfigData(tempConfigData);
        renderCurrentDrugsTab();
        dispatchDrugSettingsInput();
    } catch (error) {
        console.error("Failed to update drug ordering:", error);
        openConfirmationModal('Ordering Error', 'Failed to update the order.', () => {}, false, 'OK');
        renderCurrentDrugsTab();
    }
}

// --- Helpers for Usage Check ---

function isLocationUsed(locId) {
    const txs = appData.drugTransactions || [];
    // Check if any transaction payload references this location
    return txs.some(tx =>
        tx.payload && Array.isArray(tx.payload) && tx.payload.some(item => item.location === locId)
    );
}

function isDrugUsed(drugId) {
    const txs = appData.drugTransactions || [];
    // Check if any transaction payload references this drug
    return txs.some(tx =>
        tx.payload && Array.isArray(tx.payload) && tx.payload.some(item => item.drugId === drugId)
    );
}

// ==========================================
// 3. Roles Panel
// ==========================================
function renderDrugRolesPanel(container) {
    const roles = appData.drugRoles || [];

    container.innerHTML = `
        <div class="${ADMIN_CARD_CLASSES}">
             <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-semibold text-gray-700">Drug Roles</h3>
                 <button id="add-role-btn" class="${DRUG_PRIMARY_BUTTON_CLASSES}">
                    <span class="material-symbols-outlined text-lg">add</span>
                    Add Role
                </button>
            </div>
             <p class="text-sm text-gray-500 mb-6">Define roles specific to the Controlled Drugs system (e.g., CDO, Provider). Assign these to users below.</p>

             <div class="overflow-hidden rounded-lg border mb-8">
                <div class="overflow-x-auto">
                    <table class="w-full !table-auto text-sm text-left">
                        <thead class="bg-gray-50 text-gray-500 font-medium border-b">
                            <tr>
                                <th class="py-2 px-1.5 sm:px-4 w-14 sm:w-20 text-center"><span class="sr-only">Order</span></th>
                                <th class="py-2 px-2 sm:px-4">Role Name</th>
                                <!-- Base Access removed -->
                                <th class="py-2 px-1.5 sm:px-4 w-16 sm:w-24 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100">
                            ${renderRoleRows(roles)}
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="border-t pt-6">
                <div class="mb-6 border-b pb-6">
                    ${renderEscalationPathSection()}
                </div>

                <h4 class="text-base font-semibold text-gray-700 mb-4">User Assignments</h4>
                <div class="bg-gray-50 p-4 rounded-lg border overflow-hidden">
                     ${renderUserRoleAssignmentTable()}
                </div>
            </div>
        </div>
    `;
    bindDrugNativeFieldSurface(container);
    mountUserRoleAssignmentControls(container);

    // Listener: Add Role
    container.querySelector('#add-role-btn').addEventListener('click', () => {
        openDrugRoleForm();
    });

    container.querySelector('#btn-edit-escalation')?.addEventListener('click', () => {
        openEscalationModal(getNormalizedEscalationPath());
    });

    // Listener: Edit/Delete Role
    container.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) {return;}

        if (btn.classList.contains('drug-order-btn')) {
            handleDrugOrderingClick(btn);
        } else if (btn.classList.contains('edit-role-btn')) {
            const roleId = btn.dataset.id;
            const role = appData.drugRoles.find(r => r.id === roleId);
            if (role) {openDrugRoleForm(role);}
        }
        else if (btn.classList.contains('delete-role-btn')) {
            const roleId = btn.dataset.id;
            // Basic confirmation
            openConfirmationModal('Delete Role', 'Delete this unused role? Assigned roles must first be changed through Users Settings.', async () => {
                await saveDrugRoleSettingsCallable({roleId, remove: true});
            });
        }
    });

    // Delegated User Assignment Change
    container.querySelector('div.bg-gray-50').addEventListener('change', (e) => {
        if (e.target.classList.contains('user-role-select') || e.target.classList.contains('user-flag-check')) {
            handleUserAssignmentChange(e.target);
        }
    });
}

function renderEscalationPathSection() {
    const roles = appData.drugRoles || [];
    const escalationPath = getNormalizedEscalationPath();
    const pathItems = escalationPath.map((roleId) => {
        const role = roles.find(r => r.id === roleId);
        return `
            <li class="text-sm text-gray-700">
                <span class="font-medium text-gray-900">${sanitizeHTML(role ? role.name : roleId)}</span>
            </li>
        `;
    }).join('');

    return `
        <div class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h4 class="text-base font-semibold text-gray-700">Global Escalation Path</h4>
            <button id="btn-edit-escalation" class="${ADMIN_COMPACT_SECONDARY_BUTTON_CLASSES} w-full sm:w-auto">
                Edit Escalation Path
            </button>
        </div>
        <ol class="list-decimal space-y-1 pl-5">
            ${pathItems || '<li class="text-sm italic text-gray-500">No drug roles defined.</li>'}
        </ol>
    `;
}

function renderRoleRows(roles) {
    if (roles.length === 0) {return '<tr><td colspan="3" class="py-4 text-center text-gray-500 italic">No roles defined.</td></tr>';}

    const sortedRoles = getSortedOrderedItems(roles, {
        field: 'rank',
        tieBreaker: compareOrderLabels,
    });
    return sortedRoles.map((role, index) => {
        // Base Access removed
        return `
        <tr class="hover:bg-gray-50">
            <td class="py-1 px-1.5 sm:px-4 text-center">
                ${renderOrderControls({
        scope: 'role',
        id: role.id,
        label: role.name,
        isFirst: index === 0,
        isLast: index === sortedRoles.length - 1,
    })}
            </td>
            <td class="py-1 px-2 sm:px-4 font-medium text-gray-900 whitespace-normal break-words">${sanitizeHTML(role.name)}</td>
            <!-- Base Access cell removed -->
            <td class="py-1 px-1.5 sm:px-4 text-right whitespace-nowrap">
                <button class="edit-role-btn ${ADMIN_ICON_BUTTON_CLASSES}" title="Edit" data-id="${escapeHTML(role.id)}"><span class="material-symbols-outlined text-lg">edit</span></button>
                <button class="delete-role-btn ${ADMIN_DANGER_ICON_BUTTON_CLASSES}" title="Delete" data-id="${escapeHTML(role.id)}"><span class="material-symbols-outlined text-lg">delete</span></button>
            </td>
        </tr>`;
    }).join('');
}

function renderUserRoleAssignmentTable() {
    const users = (tempUsersData || []).filter((user) => user?.isActive !== false);

    // Sort users by their current drugSortOrder so admins see the order they are building.
    const sortedUsers = getSortedOrderedItems(users, {
        field: 'drugSortOrder',
        fallback: 999,
        tieBreaker: compareOrderLabels,
    });

    return `
    <div class="overflow-x-auto">
        <table class="w-full min-w-[640px] text-sm">
            <thead>
                <tr>
                    <th class="text-center font-medium text-gray-500 pb-2 w-16"><span class="sr-only">Order</span></th>
                    <th class="text-left font-medium text-gray-500 pb-2 w-1/3">User</th>
                    <th class="text-left font-medium text-gray-500 pb-2 w-1/3">Primary Role</th>
                    <th class="text-center font-medium text-gray-500 pb-2">Provider</th>
                    <th class="text-center font-medium text-gray-500 pb-2">Admin</th>
                </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
                ${sortedUsers.map((user) => {
        // Find the user in the original array by ID to ensure we update the correct index/object
        // Relying on indexOf object reference is brittle if copies are made elsewhere
        const originalIdx = tempUsersData.findIndex(u => u.id === user.id);
        const currentRole = user.drugRole || '';
        const visibleIndex = sortedUsers.findIndex((entry) => entry.id === user.id);

        return `
                <tr>
                    <td class="py-1 pr-2">
                        ${renderOrderControls({
        scope: 'user',
        id: user.id,
        label: user.name,
        isFirst: visibleIndex === 0,
        isLast: visibleIndex === sortedUsers.length - 1,
    })}
                    </td>
                    <td class="py-1 text-gray-900 font-medium">${sanitizeHTML(user.name)}</td>
                    <td class="py-1 pr-4">
                        <div class="user-role-select-control" data-user-idx="${originalIdx}" data-current-role="${escapeHTML(currentRole)}"></div>
                    </td>
                    <td class="py-1 text-center">
                        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} justify-center" data-native-checkbox-label>
                            <input type="checkbox" class="user-flag-check ${NATIVE_CHECKBOX_CLASSES}"
                                data-user-idx="${originalIdx}" data-flag="isProvider" ${user.isProvider ? 'checked' : ''}>
                            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                            </span>
                        </label>
                    </td>
                    <td class="py-1 text-center">
                        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} justify-center" data-native-checkbox-label>
                            <input type="checkbox" class="user-flag-check ${NATIVE_CHECKBOX_CLASSES}"
                                data-user-idx="${originalIdx}" data-flag="isDrugAdmin" ${user.isDrugAdmin ? 'checked' : ''}>
                            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                            </span>
                        </label>
                    </td>
                </tr>
                `;
    }).join('')}
            </tbody>
        </table>
    </div>`;
}

function mountUserRoleAssignmentControls(container) {
    const roles = getSortedOrderedItems(appData.drugRoles || [], { field: 'rank' });
    const roleOptions = getRoleOptions(roles, { includeNoAccess: true });

    container.querySelectorAll('.user-role-select-control').forEach((root) => {
        const userIdx = root.dataset.userIdx;
        createSelectControl({
            root,
            options: roleOptions,
            value: root.dataset.currentRole || NO_ROLE_VALUE,
            searchable: roleOptions.length >= 7,
            searchThreshold: 7,
            portalMenu: true,
            placeholder: "No Access",
            accessibleName: "Primary drug role",
            mirror: false,
            onChange: (value) => {
                const user = tempUsersData[userIdx];
                if (!user) {return;}
                user.drugRole = value === NO_ROLE_VALUE ? null : value;
                delete user.drugRoles;
                document.getElementById('drug-settings-content')
                    .dispatchEvent(new Event('input', { bubbles: true }));
            },
        });
    });
}

function handleUserAssignmentChange(input) {
    const userIdx = input.dataset.userIdx;
    const user = tempUsersData[userIdx];
    if (!user) {return;}

    if (input.classList.contains('user-flag-check')) {
        // Handle Flags
        const flag = input.dataset.flag; // isProvider or isDrugAdmin
        user[flag] = input.checked;
    }

    // Trigger Save Button
    dispatchDrugSettingsInput();
}

// ==========================================
// 4. Workflows Panel
// ==========================================
// ==========================================
// 4. Workflows Panel
// ==========================================
// ==========================================
// 4. Workflows Panel (Rule Builder)
// ==========================================
function getNormalizedEscalationPath() {
    if (!tempConfigData.drugs) {tempConfigData.drugs = {};}
    if (!tempConfigData.drugs.approvalWorkflows) {tempConfigData.drugs.approvalWorkflows = {};}

    const roles = appData.drugRoles || [];
    const workflows = tempConfigData.drugs.approvalWorkflows;

    // --- ESCALATION PATH LOGIC (Global) ---
    // Ensure all current roles are represented in the path or appended
    let escalationPath = workflows.escalationPath || [];
    const currentPathRoleIds = escalationPath;
    const missingRoles = roles.filter(r => !currentPathRoleIds.includes(r.id)).map(r => r.id);
    escalationPath = [...escalationPath, ...missingRoles];
    // Filter out deleted roles
    escalationPath = escalationPath.filter(id => roles.some(r => r.id === id));
    workflows.escalationPath = escalationPath; // Save normalized path
    return escalationPath;
}

function renderWorkflowsPanel(container) {
    getNormalizedEscalationPath();

    // Definition of available workflows
    const workflowDefs = [
        { key: 'drug_use', label: 'Log Usage', icon: 'vaccines' },
        { key: 'drug_relocate', label: 'Relocate Stock', icon: 'swap_horiz' },
        { key: 'drug_correction', label: 'Corrections', icon: 'edit' },
        { key: 'drug_restock', label: 'Restock', icon: 'inventory_2' },
        { key: 'drug_loss', label: 'Log Loss', icon: 'warning' },
        { key: 'drug_void_transaction', label: 'Void Transaction', icon: 'backspace' },
        { key: 'drug_audit_start', label: 'Start Audit', icon: 'play_circle' },
        { key: 'drug_audit_finalize', label: 'Finalise Audit', icon: 'check_circle' },
        { key: 'drug_audit_cancel', label: 'Cancel Audit', icon: 'cancel' }
    ];
    const selectedWorkflow = workflowDefs.find(def => def.key === selectedWorkflowKey) || workflowDefs[0];
    selectedWorkflowKey = selectedWorkflow.key;

    container.innerHTML = `
        <div class="${ADMIN_CARD_CLASSES} h-full flex flex-col">
             <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-700">Approval Workflows</h3>
                <p class="text-sm text-gray-500">Configure "If/Then" rules for each transaction type.</p>
             </div>

             <!-- Split Pane Layout -->
             <div class="flex flex-grow flex-col overflow-hidden rounded-lg border md:h-[600px] md:flex-row">

                <!-- MOBILE: Workflow Selector -->
                <div class="border-b bg-white p-3 md:hidden">
                    <details id="workflow-mobile-switcher" class="feature-nav-switcher">
                        <summary class="feature-nav-switcher__summary" aria-label="Select approval workflow">
                            <span class="feature-nav-switcher__active-label min-w-0 truncate">${selectedWorkflow.label}</span>
                            <span class="material-symbols-outlined text-[20px]" aria-hidden="true">chevron_right</span>
                        </summary>
                        <div class="feature-nav-switcher__menu">
                            ${workflowDefs.map(def => {
                                const isActive = selectedWorkflowKey === def.key;
                                return `
                                    <button
                                        type="button"
                                        class="workflow-select-btn feature-nav-switcher__option ${isActive ? 'is-active' : ''}"
                                        data-key="${def.key}"
                                        aria-current="${isActive ? 'page' : 'false'}"
                                    >
                                        <span class="material-symbols-outlined text-[18px] text-gray-400" aria-hidden="true">${def.icon}</span>
                                        <span class="min-w-0 flex-1 truncate">${def.label}</span>
                                        ${isActive ? '<span class="material-symbols-outlined text-[18px]" aria-hidden="true">check</span>' : ''}
                                    </button>
                                `;
                            }).join('')}
                        </div>
                    </details>
                </div>

                <!-- LEFT: Workflow Selector -->
                <div class="hidden w-1/4 bg-gray-50 border-r md:flex flex-col overflow-y-auto">
                    ${workflowDefs.map(def => `
                        <button class="workflow-select-btn w-full text-left px-4 py-3 border-b hover:bg-white transition-colors flex items-center space-x-3
                            ${selectedWorkflowKey === def.key ? 'bg-white border-l-4 border-l-[var(--dfc-brand-accent)] shadow-sm' : 'text-gray-600 border-l-4 border-l-transparent'}"
                            data-key="${def.key}">
                            <span class="material-symbols-outlined text-gray-400">${def.icon}</span>
                            <span class="font-medium text-sm">${def.label}</span>
                        </button>
                    `).join('')}

                </div>

                <!-- RIGHT: Configuration Panel -->
                <div class="w-full min-w-0 bg-white flex flex-col overflow-y-auto md:w-3/4" id="workflow-config-panel">
                    <!-- Dynamic Content -->
                </div>
             </div>
        </div>
    `;
    bindDrugNativeFieldSurface(container);

    // Render Right Panel
    const rightPanel = container.querySelector('#workflow-config-panel');
    renderWorkflowConfigPanel(selectedWorkflowKey, rightPanel);

    // Listeners: Selection
    container.querySelectorAll('.workflow-select-btn').forEach(btn => {
        btn.addEventListener('click', (_e) => {
            selectedWorkflowKey = btn.dataset.key;
            renderWorkflowsPanel(container); // Re-render whole panel to update selection active state
        });
    });
}

function renderWorkflowConfigPanel(key, container) {
    const workflow = tempConfigData.drugs.approvalWorkflows[key] || {};
    const rules = workflow.rules || [];

    // Default Values
    const defaultMode = workflow.defaultMode || 'pre_action';
    const timeoutDays = workflow.timeoutDays || 3;

    container.innerHTML = `
        <div class="p-4 sm:p-6">
            <div class="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
                <h4 class="text-base font-bold text-gray-800 sm:text-lg">Rules Engine: ${key.replace('drug_', '').toUpperCase()}</h4>
                <button id="add-rule-btn" class="${ADMIN_COMPACT_PRIMARY_BUTTON_CLASSES}">
                    <span class="material-symbols-outlined text-lg">add_circle</span>
                    Add Rule
                </button>
            </div>

            <!-- Rules List -->
            <div class="space-y-3 mb-8">
                ${renderRuleList(rules)}
            </div>

            <!-- Fallback Settings -->
            <div class="border-t pt-6">
                <h5 class="text-sm font-bold text-gray-700 mb-3 flex items-center">
                    <span class="material-symbols-outlined text-gray-400 mr-2 text-lg">shield</span>
                    Fallback Policy (If no rules match)
                </h5>
                <div class="bg-gray-50 p-4 rounded border grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-base text-gray-500 sm:text-sm mb-1">Default Action (Safety Net)</label>
                        <div id="default-mode-control"></div>
                        <p class="text-xs text-gray-500 mt-1" data-native-field-skip="true">Applied if no rules match. 'Reject' is recommended for security.</p>
                    </div>
                    <div>
                        <label class="block text-base text-gray-500 sm:text-sm mb-1">Default Timeout (Days)</label>
                        <input type="number" id="default-timeout-input" class="${DRUG_MODAL_INPUT_CLASSES}" value="${timeoutDays}">
                    </div>
                </div>
            </div>

        </div>
    `;
    applyDrugControlSurface(container, appData);
    bindDrugNativeFieldSurface(container);

    // Listeners: Add Rule
    container.querySelector('#add-rule-btn').addEventListener('click', () => {
        openRuleEditor(key, null); // Add New
    });

    // Listeners: Edit/Delete Rule
    container.querySelectorAll('.edit-rule-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const ruleId = btn.dataset.id;
            const rule = rules.find(r => r.id === ruleId);
            if (rule) {openRuleEditor(key, rule);}
        });
    });

    container.querySelectorAll('.delete-rule-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const ruleId = btn.dataset.id;
            openConfirmationModal(
                'Delete Rule',
                'Are you sure you want to delete this rule? This action cannot be undone.',
                () => deleteRule(key, ruleId),
                true,
                'Delete'
            );
        });
    });

    // Listeners: Reorder Rules
    container.querySelectorAll('.move-rule-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const ruleId = btn.dataset.id;
            const direction = btn.dataset.dir; // 'up' or 'down'
            moveRule(key, ruleId, direction);
        });
    });

    // Listeners: Default Settings Change
    const saveDefaults = () => {
        if (!tempConfigData.drugs.approvalWorkflows[key]) {tempConfigData.drugs.approvalWorkflows[key] = {};}

        tempConfigData.drugs.approvalWorkflows[key].defaultMode = defaultModeControl.value;
        tempConfigData.drugs.approvalWorkflows[key].timeoutDays = Number(container.querySelector('#default-timeout-input').value);

        document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
    };

    const defaultModeControl = createSelectControl({
        root: container.querySelector('#default-mode-control'),
        options: getApprovalModeOptions(key),
        value: defaultMode,
        searchable: false,
        portalMenu: true,
        accessibleName: 'Default approval action',
        mirror: false,
        onChange: saveDefaults,
    });
    container.querySelector('#default-timeout-input').addEventListener('change', saveDefaults);
}

function renderRuleList(rules) {
    if (rules.length === 0) {
        return `<div class="p-4 border border-dashed rounded text-center text-gray-400 text-sm">No rules defined. System will use Fallback Policy.</div>`;
    }

    // Sort by priority
    const sorted = getSortedOrderedItems(rules, { field: 'priority' });

    return sorted.map((rule, i) => {
        const isFirst = i === 0;
        const isLast = i === sorted.length - 1;
        // --- Info Display Construction ---
        const infoParts = [];

        // 1. Actor Role
        const roleName = rule.if?.actorRole ?
            appData.drugRoles.find(r => r.id === rule.if.actorRole)?.name || rule.if.actorRole :
            'Any role';
        infoParts.push(`<span class="text-gray-600">Actor role:</span> <span class="font-medium text-gray-900">${sanitizeHTML(roleName)}</span>`);

        // 2. Scope
        if (rule.if?.context) {
            let contextLabel = rule.if.context;
            if (contextLabel === 'match_target') {contextLabel = 'Own Work';}
            else if (contextLabel === 'mismatch_target') {contextLabel = "Others' Work";}

            infoParts.push(`<span class="text-gray-600">Scope:</span> <span class="font-medium text-gray-900">${contextLabel}</span>`);
        }

        // 3. Approval Mode (Re-coloring)
        const modeMap = {
            'reject': 'Reject',
            'pre_action': 'Hard Block',
            'post_action': 'Soft Approve',
            'bypass': 'Auto-Approve'
        };
        const modeLabel = modeMap[rule.then.approvalMode] || rule.then.approvalMode;

        let modeColorClass = 'text-gray-900';
        if (rule.then.approvalMode === 'bypass') {modeColorClass = 'text-green-600';}
        else if (rule.then.approvalMode === 'pre_action') {modeColorClass = 'text-orange-600';}
        else if (rule.then.approvalMode === 'post_action') {modeColorClass = 'text-blue-600';}
        else if (rule.then.approvalMode === 'reject') {modeColorClass = 'text-red-600';}

        infoParts.push(`<span class="text-gray-600">Approval mode:</span> <span class="font-medium ${modeColorClass}">${modeLabel}</span>`);

        // 4. Target Approver (if applicable)
        if (rule.then.approvalMode !== 'bypass' && rule.then.approvalMode !== 'reject') {
            let targetLabel = rule.then.approverTarget;
            if (targetLabel === 'escalation_path') {targetLabel = 'Escalation Path';}
            else if (targetLabel === 'field:provider') {targetLabel = 'Target User';} // Kept rename
            else {
                // Try to find role name
                const rName = appData.drugRoles.find(r => r.id === targetLabel)?.name || targetLabel;
                targetLabel = rName;
            }
            if (targetLabel) {
                infoParts.push(`<span class="text-gray-600">Target approver:</span> <span class="font-medium text-gray-900">${sanitizeHTML(targetLabel)}</span>`);
            }
        }

        // 5. Timeout (if applicable)
        if (rule.then.timeoutDays && rule.then.approvalMode !== 'bypass' && rule.then.approvalMode !== 'reject') {
            infoParts.push(`<span class="text-gray-600">Timeout:</span> <span class="font-medium text-gray-900">${rule.then.timeoutDays} days</span>`);
        }

        const infoString = infoParts
            .map(part => `<span class="inline-flex flex-wrap gap-x-1">${part}</span>`)
            .join('');

        return `
            <div class="bg-white border rounded p-3 flex flex-col gap-3 shadow-sm relative group mb-2 sm:flex-row sm:items-center sm:justify-between">
                <div class="absolute left-0 top-0 bottom-0 w-1 ${rule.then.approvalMode === 'bypass' ? 'bg-green-400' : (rule.then.approvalMode === 'reject' ? 'bg-red-500' : (rule.then.approvalMode === 'post_action' ? 'bg-blue-400' : 'bg-orange-400'))} rounded-l"></div>

                <div class="pl-3 flex-1 min-w-0">
                    <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400 mb-1">
                        <span class="font-mono bg-gray-100 px-1 rounded">#${rule.priority || i + 1}</span>
                        <span>${sanitizeHTML(rule.description || 'No description')}</span>
                    </div>
                    <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        ${infoString}
                    </div>
                </div>

                <div class="flex items-center justify-end space-x-1 pl-3 sm:pl-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                     <!-- Reorder Controls -->
                    <div class="drug-order-controls flex flex-col items-center mr-2" aria-label="Rule order controls">
                        <button type="button" class="move-rule-btn ${ADMIN_ORDER_BUTTON_CLASSES}" data-id="${escapeHTML(rule.id)}" data-dir="up" title="Move Up" aria-label="Move rule up" ${isFirst ? 'disabled' : ''}>
                            <span class="material-symbols-outlined text-[18px] leading-none">expand_less</span>
                        </button>
                        <button type="button" class="move-rule-btn ${ADMIN_ORDER_BUTTON_CLASSES}" data-id="${escapeHTML(rule.id)}" data-dir="down" title="Move Down" aria-label="Move rule down" ${isLast ? 'disabled' : ''}>
                            <span class="material-symbols-outlined text-[18px] leading-none">expand_more</span>
                        </button>
                    </div>

                    <button class="edit-rule-btn ${ADMIN_ICON_BUTTON_CLASSES}" data-id="${escapeHTML(rule.id)}"><span class="material-symbols-outlined text-lg">edit</span></button>
                    <button class="delete-rule-btn ${ADMIN_DANGER_ICON_BUTTON_CLASSES}" data-id="${escapeHTML(rule.id)}"><span class="material-symbols-outlined text-lg">delete</span></button>
                </div>
            </div>
        `;
    }).join('');
}

// --- Rule Editor Modal (Lightweight Custom Implementation) ---
function openRuleEditor(workflowKey, existingRule) {
    const roles = appData.drugRoles || [];
    const actorOptions = [
        { value: ANY_ROLE_VALUE, label: 'Any Role', order: -1 },
        ...getRoleOptions(roles),
    ];
    const targetOptions = getRuleTargetOptions(roles, ['drug_use', 'drug_relocate', 'drug_void_transaction'].includes(workflowKey));

    // Get current max priority for default
    const currentRules = tempConfigData.drugs.approvalWorkflows[workflowKey]?.rules || [];
    const maxPriority = currentRules.length > 0 ? Math.max(...currentRules.map(r => r.priority || 0)) : 0;

    // Default Rule State
    const rule = existingRule || {
        id: `rule_${Date.now()}`,
        priority: maxPriority + 1,
        description: "",
        if: { actorRole: "", context: "" },
        then: { approvalMode: "pre_action", approverTarget: "escalation_path", timeoutDays: 3 }
    };

    // Determine valid scopes based on transaction type (Target Identity Valid types)
    // Use/Relocate = Target is Provider
    // Void = Target is Original Author
    const isTargetContextValid = ['drug_use', 'drug_relocate', 'drug_void_transaction'].includes(workflowKey);

    const title = existingRule ? 'Edit Rule' : 'Add New Rule';

    // HTML Content for Modal
    const content = `
        <div class="space-y-3 sm:space-y-4">
            <!-- Metadata -->
            <div class="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-4">
                <div class="md:col-span-3">
                    <label class="block text-base text-gray-500 sm:text-sm mb-1">Description (Internal)</label>
                    <input type="text" id="rule-desc" class="${DRUG_MODAL_INPUT_CLASSES}" value="${escapeHTML(rule.description)}" placeholder="e.g. CDO Fast Track">
                </div>
                <div>
                     <label class="block text-base text-gray-500 sm:text-sm mb-1">Priority</label>
                     ${getReadOnlyValueHtml(String(rule.priority), { id: 'rule-prio-display' })}
                    <input type="hidden" id="rule-prio" value="${rule.priority}" data-native-field-skip="true">
                </div>
            </div>

            <hr class="border-gray-200">

            <!-- CONDITION (IF) -->
            <h5 class="text-sm font-bold text-gray-700">IF Condition</h5>
            <div class="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                <div>
                    <label class="block text-base text-gray-500 sm:text-sm mb-1">Actor Role</label>
                    <div id="rule-actor-control"></div>
                </div>
                <div>
                    <label class="block text-base text-gray-500 sm:text-sm mb-1">Scope ${!isTargetContextValid ? '<span class="text-gray-400 font-normal">(N/A)</span>' : ''}</label>
                    <div id="rule-context-control"></div>
                </div>
            </div>

            <hr class="border-gray-200">

            <!-- CONSEQUENCE (THEN) -->
             <h5 class="text-sm font-bold text-gray-700">THEN Consequence</h5>
             <div class="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                <div>
                    <label class="block text-base text-gray-500 sm:text-sm mb-1">Approval Mode</label>
                    <div id="rule-mode-control"></div>
                </div>
                <div>
                    <label class="block text-base text-gray-500 sm:text-sm mb-1">Target Approver</label>
                    <div id="rule-target-control"></div>
                </div>
                <div>
                     <label class="block text-base text-gray-500 sm:text-sm mb-1">Timeout (Days)</label>
                    <input type="number" id="rule-timeout" class="${DRUG_MODAL_INPUT_CLASSES}" value="${rule.then.timeoutDays ?? ''}" ${['bypass', 'reject'].includes(rule.then.approvalMode) ? 'disabled' : ''}>
                </div>
            </div>
        </div>
    `;

    // Using `openAdminModal` pattern (mocked here directly into body)

    const modalHtml = `
        <div id="rule-editor-modal" class="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div class="bg-white rounded-lg shadow-xl p-4 w-full max-w-[calc(100vw-2rem)] flex flex-col max-h-[90vh] min-w-0 sm:m-4 sm:max-w-2xl sm:p-6">
                <div class="mb-6 flex items-center justify-between gap-3 px-2 shrink-0">
                    <h3 class="text-2xl font-semibold text-gray-900 shrink-0">${title}</h3>
                </div>
                <div class="min-w-0 space-y-4 overflow-y-auto px-2 py-1 flex-1 min-h-0">
                    ${content}
                </div>
                <div class="mt-6 flex justify-end space-x-4 px-2 shrink-0">
                    <button id="cancel-rule-btn" class="${ADMIN_MODAL_SECONDARY_BUTTON_CLASSES}">Cancel</button>
                    <button id="save-rule-btn" class="${ADMIN_MODAL_PRIMARY_BUTTON_CLASSES}">Save Rule</button>
                </div>
            </div>
        </div>
    `;

    // Append to body and manage lifecycle
    const wrapper = document.createElement('div');
    wrapper.innerHTML = modalHtml;
    document.body.appendChild(wrapper.firstElementChild);

    const modal = document.getElementById('rule-editor-modal');
    applyDrugControlSurface(modal, appData);
    bindDrugNativeFieldSurface(modal);

    // UI Logic: Disable Target/Timeout if Bypass
    const timeoutInput = modal.querySelector('#rule-timeout');
    const actorControl = createSelectControl({
        root: modal.querySelector('#rule-actor-control'),
        options: actorOptions,
        value: rule.if.actorRole || ANY_ROLE_VALUE,
        searchable: actorOptions.length >= 7,
        searchThreshold: 7,
        portalMenu: true,
        accessibleName: "Rule actor role",
        placeholder: "Any Role",
        mirror: false,
    });
    const contextControl = createSelectControl({
        root: modal.querySelector('#rule-context-control'),
        options: [
            { value: GLOBAL_CONTEXT_VALUE, label: "None (Global)" },
            { value: "match_target", label: "Acting on Own Work/Assignment" },
            { value: "mismatch_target", label: "Acting on Others' Work/Assignment" },
        ],
        value: rule.if.context || GLOBAL_CONTEXT_VALUE,
        disabled: !isTargetContextValid,
        searchable: false,
        portalMenu: true,
        accessibleName: "Rule scope",
        placeholder: "None (Global)",
        mirror: false,
    });
    const modeControl = createSelectControl({
        root: modal.querySelector('#rule-mode-control'),
        options: getRuleApprovalModeOptions(workflowKey),
        value: rule.then.approvalMode,
        searchable: false,
        portalMenu: true,
        accessibleName: "Rule approval mode",
        mirror: false,
        onChange: (value) => {
            const isBypass = value === 'bypass';
            const isReject = value === 'reject';
            targetControl.update({ disabled: isBypass || isReject });
            timeoutInput.disabled = isBypass || isReject;
        },
    });
    const targetControl = createSelectControl({
        root: modal.querySelector('#rule-target-control'),
        options: targetOptions,
        value: rule.then.approverTarget || "escalation_path",
        searchable: targetOptions.length >= 7,
        searchThreshold: 7,
        portalMenu: true,
        disabled: ['bypass', 'reject'].includes(rule.then.approvalMode),
        accessibleName: "Rule target approver",
        mirror: false,
    });

    // Close Logic
    const close = () => {
        destroyRegisteredControlsIn(modal);
        modal.remove();
    };
    modal.querySelector('#cancel-rule-btn').onclick = close;

    // Save Logic
    modal.querySelector('#save-rule-btn').onclick = () => {
        const updatedRule = {
            id: rule.id,
            description: modal.querySelector('#rule-desc').value,
            priority: Number(modal.querySelector('#rule-prio').value),
            if: {
                actorRole: actorControl.value === ANY_ROLE_VALUE ? null : actorControl.value,
                context: contextControl.value === GLOBAL_CONTEXT_VALUE ? null : contextControl.value
            },
            then: {
                approvalMode: modeControl.value,
                approverTarget: (modeControl.value === 'bypass' || modeControl.value === 'reject') ? null : targetControl.value,
                timeoutDays: (modeControl.value === 'bypass' || modeControl.value === 'reject') ? null : Number(timeoutInput.value)
            }
        };

        saveRule(workflowKey, updatedRule);
        close();
    };
}

function saveRule(key, rule) {
    if (!tempConfigData.drugs.approvalWorkflows[key]) {tempConfigData.drugs.approvalWorkflows[key] = { rules: [] };}
    const workflow = tempConfigData.drugs.approvalWorkflows[key];
    if (!workflow.rules) {workflow.rules = [];}

    const idx = workflow.rules.findIndex(r => r.id === rule.id);
    if (idx >= 0) {
        workflow.rules[idx] = rule;
    } else {
        workflow.rules.push(rule);
    }

    // Refresh UI
    const panel = document.getElementById('workflow-config-panel');
    if (panel) {renderWorkflowConfigPanel(selectedWorkflowKey, panel);}

    // Re-sequence rules (just in case)
    resequenceRules(key);
}

function deleteRule(key, ruleId) {
    const rules = tempConfigData.drugs.approvalWorkflows[key]?.rules || [];
    tempConfigData.drugs.approvalWorkflows[key].rules = rules.filter(r => r.id !== ruleId);

    // Re-sequence after deletion to fill gaps
    resequenceRules(key);
}

function moveRule(key, ruleId, direction) {
    const workflow = tempConfigData.drugs.approvalWorkflows[key];
    if (!workflow?.rules) {return;}

    const moved = moveOrderedItem(workflow.rules, ruleId, direction === 'up' ? -1 : 1, {
        field: 'priority',
    });
    if (!moved) {return;}

    const panel = document.getElementById('workflow-config-panel');
    if (panel) {renderWorkflowConfigPanel(selectedWorkflowKey, panel);}
    dispatchDrugSettingsInput();
}

function resequenceRules(key) {
    const workflow = tempConfigData.drugs.approvalWorkflows[key];
    if (!workflow) {return;}
    const rules = getSortedOrderedItems(workflow.rules || [], { field: 'priority' });
    // Ensure 1..N sequence based on current array order
    rules.forEach((r, idx) => {
        r.priority = idx + 1;
    });
    workflow.rules = rules;

    const panel = document.getElementById('workflow-config-panel');
    if (panel) {renderWorkflowConfigPanel(selectedWorkflowKey, panel);}
    document.getElementById('drug-settings-content').dispatchEvent(new Event('input', { bubbles: true }));
}

function openEscalationModal(currentPath) {
    const roles = appData.drugRoles || [];
    const content = `
        <div id="escalation-sort-list" class="space-y-2">
            ${currentPath.map((roleId, idx) => {
        const role = roles.find(r => r.id === roleId);
        const roleName = role ? role.name : roleId;
        return `
                <div class="flex items-center gap-3 bg-white border p-2 rounded" data-escalation-role-id="${escapeHTML(roleId)}">
                    <div class="drug-order-controls flex flex-col items-center" aria-label="Escalation path order controls">
                        <button type="button" class="escalation-order-btn ${ADMIN_ORDER_BUTTON_CLASSES}" data-direction="-1" title="Move up" aria-label="Move ${escapeHTML(roleName)} up" ${idx === 0 ? 'disabled' : ''}>
                            <span class="material-symbols-outlined text-[18px] leading-none">expand_less</span>
                        </button>
                        <button type="button" class="escalation-order-btn ${ADMIN_ORDER_BUTTON_CLASSES}" data-direction="1" title="Move down" aria-label="Move ${escapeHTML(roleName)} down" ${idx === currentPath.length - 1 ? 'disabled' : ''}>
                            <span class="material-symbols-outlined text-[18px] leading-none">expand_more</span>
                        </button>
                    </div>
                    <span class="font-medium text-sm">${sanitizeHTML(roleName)}</span>
                </div>
                `;
    }).join('')}
        </div>
    `;

    openConfirmationModal('Edit Escalation Path', content, () => {
        const newPath = Array.from(document.querySelectorAll('[data-escalation-role-id]'))
            .map(row => row.dataset.escalationRoleId);

        tempConfigData.drugs.approvalWorkflows.escalationPath = newPath;
        dispatchDrugSettingsInput();
        renderCurrentDrugsTab();
    }, false, 'Save Order');
    const confirmModal = document.getElementById('confirm-modal');
    applyDrugControlSurface(confirmModal, appData);
    bindDrugNativeFieldSurface(confirmModal);

    const list = confirmModal?.querySelector('#escalation-sort-list');
    const refreshButtons = () => {
        const rows = Array.from(list?.querySelectorAll('[data-escalation-role-id]') || []);
        rows.forEach((row, index) => {
            const up = row.querySelector('[data-direction="-1"]');
            const down = row.querySelector('[data-direction="1"]');
            if (up) {up.disabled = index === 0;}
            if (down) {down.disabled = index === rows.length - 1;}
        });
    };
    list?.addEventListener('click', (event) => {
        const button = event.target.closest('.escalation-order-btn');
        if (!button) {return;}
        const row = button.closest('[data-escalation-role-id]');
        const direction = Number(button.dataset.direction);
        if (!row || !Number.isFinite(direction)) {return;}

        if (direction < 0 && row.previousElementSibling) {
            list.insertBefore(row, row.previousElementSibling);
        } else if (direction > 0 && row.nextElementSibling) {
            list.insertBefore(row.nextElementSibling, row);
        }
        refreshButtons();
    });
}
